/**
 * Slice 5 — `DatabaseListingFallbackBanner`.
 *
 * The universal explainer + action row shown above the smart-fallback list
 * on every picker surface that hits the listing-failure path. Three layout
 * variants — `row`, `subline`, `block` — keep the wording / wiring identical
 * across surfaces while letting each adapt to its own density:
 *
 *   - `'row'`     — Object Explorer tree banner (used as the standalone
 *                   error-tile companion).
 *   - `'subline'` — dropdown header sub-line (Table Explorer / schema-
 *                   compare DB select / common DB selector). Compact.
 *   - `'block'`   — modal picker inline notice block (schema/data-compare
 *                   setup dialog, table-designer dialog).
 *
 * The component owns the post-send `Sent ✓` flip and the privacy-mode
 * branch (`crashReportMode === 'never'` hides the send button entirely
 * and replaces it with a one-liner pointing to Privacy settings).
 *
 * Send wiring: per PRD Slice 5 acceptance criterion, the button fires
 * `analytics:send-diagnostic-report` directly — no need to open the dialog
 * first. The redactor (Slice 4) runs unchanged inside `sendDiagnosticReport`.
 */

import { useState } from 'react';
import { AlertTriangle, Send, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../store/appStore';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type { DiagnosticsTrigger } from '../../../types/connectionDiagnostics';
import {
  showConnectionDiagnostics,
  type ShowConnectionDiagnosticsArgs,
} from '../showConnectionDiagnostics';
import {
  FALLBACK_BANNER_HEADLINE,
  FALLBACK_PRIVACY_NOTICE,
} from '../utils/fallbackTooltip';
import { readLastListingAdapter } from '../utils/readLastListingAdapter';
import {
  useFallbackBannerSentStore,
  useFallbackBannerSendStatus,
} from './fallbackBannerSentStore';

type BannerVariant = 'row' | 'subline' | 'block';

type DatabaseListingFallbackBannerProps = {
  connectionId: string;
  variant: BannerVariant;
  /**
   * Defaults to `'list_failure'`. Pickers showing the banner because of an
   * `'empty'` listing should pass `'list_empty'` so the dialog headline
   * differentiates the two cases.
   */
  trigger?: ShowConnectionDiagnosticsArgs['trigger'];
  /** Run before opening the dialog — e.g. close a parent dropdown. */
  beforeOpenDetails?: () => void;
  className?: string;
  'data-testid'?: string;
  /** Optional testid override for the *Show details* button (back-compat). */
  showDetailsTestId?: string;
  /** Optional testid override for the *Send diagnostic report* button. */
  sendTestId?: string;
};

export function DatabaseListingFallbackBanner({
  connectionId,
  variant,
  trigger = 'list_failure',
  beforeOpenDetails,
  className,
  'data-testid': dataTestId,
  showDetailsTestId,
  sendTestId,
}: DatabaseListingFallbackBannerProps) {
  const crashReportMode = useAppStore((s) => s.crashReportMode);
  const sendStatus = useFallbackBannerSendStatus(connectionId);
  const setStatus = useFallbackBannerSentStore((s) => s.setStatus);
  const { track } = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sendIpcAvailable =
    typeof window !== 'undefined' &&
    !!window.electronAPI?.runConnectionDiagnostics &&
    !!window.electronAPI?.sendDiagnosticReport;
  const showSendButton = crashReportMode !== 'never' && sendIpcAvailable;

  const handleShowDetails = () => {
    beforeOpenDetails?.();
    showConnectionDiagnostics({ connectionId, trigger });
  };

  const handleSend = async () => {
    if (sendStatus === 'sending' || sendStatus === 'sent') return;
    if (!sendIpcAvailable) return;
    setErrorMessage(null);
    setStatus(connectionId, 'sending');
    try {
      // Map `'list_empty'` onto the IPC's underlying `'list_failure'` enum
      // (mirrors the dialog's `apiTrigger` mapping in Slice 5b).
      const apiTrigger: DiagnosticsTrigger =
        trigger === 'list_empty'
          ? 'list_failure'
          : (trigger as DiagnosticsTrigger);
      const lastListingAdapter = readLastListingAdapter(connectionId);
      const report = await window.electronAPI!.runConnectionDiagnostics({
        connectionId,
        trigger: apiTrigger,
        ...(lastListingAdapter ? { lastListingAdapter } : {}),
      });
      const result = await window.electronAPI!.sendDiagnosticReport({ report });
      if (result?.success) {
        setStatus(connectionId, 'sent');
        track('feature:connection_diagnostics_send', { from: 'banner' });
      } else {
        setStatus(connectionId, 'failed');
        setErrorMessage('Send failed. Try again.');
      }
    } catch {
      setStatus(connectionId, 'failed');
      setErrorMessage('Send failed. Try again.');
    }
  };

  const containerClass =
    variant === 'subline'
      ? 'flex flex-col gap-1 px-2 py-1.5'
      : variant === 'block'
        ? 'flex flex-col gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2'
        : 'flex flex-col gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5';

  const textClass = variant === 'subline' ? 'text-[11px]' : 'text-xs';
  const iconClass = variant === 'subline' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <div
      className={cn(containerClass, className)}
      data-testid={dataTestId ?? 'database-listing-fallback-banner'}
      role="status"
    >
      <div className={cn('flex items-start gap-1.5 text-foreground', textClass)}>
        <AlertTriangle
          className={cn('mt-0.5 shrink-0 text-warning', iconClass)}
          aria-hidden
        />
        <span
          className="flex-1"
          data-testid="database-listing-fallback-banner-headline"
        >
          {FALLBACK_BANNER_HEADLINE}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleShowDetails();
          }}
          data-testid={showDetailsTestId ?? 'database-listing-fallback-banner-show-details'}
        >
          Show details
        </Button>
        {showSendButton ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            disabled={sendStatus === 'sending' || sendStatus === 'sent'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleSend();
            }}
            data-testid={sendTestId ?? 'database-listing-fallback-banner-send'}
            data-send-status={sendStatus}
          >
            {sendStatus === 'sent' ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden />
                Sent
              </>
            ) : sendStatus === 'sending' ? (
              <>Sending…</>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" aria-hidden />
                Send diagnostic report
              </>
            )}
          </Button>
        ) : (
          <span
            className="text-[11px] text-muted-foreground"
            data-testid="database-listing-fallback-banner-privacy"
          >
            {FALLBACK_PRIVACY_NOTICE}
          </span>
        )}
        {errorMessage ? (
          <span
            className="text-[11px] text-destructive"
            data-testid="database-listing-fallback-banner-error"
          >
            {errorMessage}
          </span>
        ) : null}
      </div>
    </div>
  );
}
